import * as React from "react";
import {useTranslation} from "react-i18next";
import {Box, Button, Grid, Typography} from "@material-ui/core";
import {Alert} from "@material-ui/lab";
import GetAppIcon from "@material-ui/icons/GetApp";
import {ExternalApiClient} from "../../../../model/ExternalApiClient";
import {FormField, FormInputType} from "../../../../raal_components/form/Form";
import {Dial} from "../../../../raal_components/dial/Dial";
import {CheckMark} from "../../../../../common/component/CheckMark";
import {useCodeBookDetailContextNew} from "../../../../raal_components/controller/CodeBookDetail";
import {RouteComponentProps} from "../../../../routes";
import {ExternalApiCityOverridesEditor} from "./ExternalApiCityOverridesEditor";
import {ExternalApiAllowedIpsEditor} from "./ExternalApiAllowedIpsEditor";
import {ExternalApiTester} from "./ExternalApiTester";
import {downloadFileSimple} from "../../../../../common/utils/DownloadFile";
import {useErrorTranslator} from "../../../../../common/utils/error-utils";

const enumOptions = (values: readonly string[]) => ({
    options: [...values],
    isClearable: false,
});

const dtoVariantProps = enumOptions(["GENERAL", "WALTER", "SCHENKER", "SCHENKER_V2"]);
const countryCodeModeProps = enumOptions(["SELF_FIXING", "PASSTHROUGH", "MPZ_FROM_ISO"]);
const dateAlignmentModeProps = enumOptions(["NONE", "FULL_DAY", "START_OF_DAY"]);
const ipAccessModeProps = enumOptions(["ALL", "WHITELIST"]);

const SectionHeader: React.FC<{title: string; description: string}> = ({title, description}) => (
    <Box style={{marginTop: 16, marginBottom: 4}}>
        <Typography variant="h6" component="h3" style={{marginBottom: 4}}>{title}</Typography>
        <Typography variant="body2" color="textSecondary">{description}</Typography>
    </Box>
);

const ExternalApiClientForm = () => {
    const {t} = useTranslation();
    const {data} = useCodeBookDetailContextNew<ExternalApiClient>();
    const errorTranslator = useErrorTranslator();
    const isExisting = !!data?.id;
    const canDownloadOpenApi = isExisting && data?.dtoVariant === "GENERAL";

    return (
        <Grid container spacing={2}>
            {/* Globální popis */}
            <Grid item xs={12}>
                <Alert severity="info" style={{marginBottom: 8}}>
                    <Typography variant="body2">
                        {t("ExternalApiClient.GeneralIntro")}
                    </Typography>
                </Alert>
            </Grid>

            {/* === Základní === */}
            <Grid item xs={12}>
                <SectionHeader title={t("ExternalApiClient.SectionBasic")} description={t("ExternalApiClient.SectionBasicDescription")}/>
            </Grid>
            <Grid item lg={3} sm={4} xs={12}>
                <FormField
                    name="slug"
                    title={t("ExternalApiClient.slug")}
                    type="text"
                    required
                    textFieldProps={{inputProps: {maxLength: 64, readOnly: isExisting}}}
                    dataTip={t("ExternalApiClient.SlugTooltip")}
                />
            </Grid>
            <Grid item lg={5} sm={6} xs={12}>
                <FormField
                    name="name"
                    title={t("ExternalApiClient.name")}
                    type="text"
                    textFieldProps={{inputProps: {maxLength: 255}}}
                    dataTip={t("ExternalApiClient.NameTooltip")}
                />
            </Grid>
            <Grid item lg={2} sm={4} xs={6}>
                <FormField
                    name="enabled"
                    title={t("ExternalApiClient.enabled")}
                    type="checkbox"
                    dataTip={t("ExternalApiClient.EnabledTooltip")}
                />
            </Grid>
            <Grid item lg={2} sm={4} xs={6}>
                <FormField
                    name="provozovnaKod"
                    title={t("ExternalApiClient.provozovnaKod")}
                    type="text"
                    textFieldProps={{inputProps: {maxLength: 6}}}
                    dataTip={t("ExternalApiClient.ProvozovnaKodTooltip")}
                />
            </Grid>
            <Grid item lg={2} sm={4} xs={6}>
                <FormField
                    name="provozovnaId"
                    title={t("ExternalApiClient.provozovnaIdReadonly")}
                    type="number"
                    textFieldProps={{InputProps: {readOnly: true}}}
                    dataTip={t("ExternalApiClient.ProvozovnaIdReadonlyTooltip")}
                />
            </Grid>

            {/* === Mapování dat === */}
            <Grid item xs={12}>
                <SectionHeader title={t("ExternalApiClient.SectionMapping")} description={t("ExternalApiClient.SectionMappingDescription")}/>
            </Grid>
            <Grid item lg={3} sm={4} xs={12}>
                <FormField
                    name="dtoVariant"
                    title={t("ExternalApiClient.dtoVariant")}
                    type="select"
                    selectProps={dtoVariantProps}
                    dataTip={t("ExternalApiClient.DtoVariantTooltip")}
                />
            </Grid>
            <Grid item lg={2} sm={4} xs={6}>
                <FormField
                    name="weightDivider"
                    title={t("ExternalApiClient.weightDivider")}
                    type="number"
                    required
                    dataTip={t("ExternalApiClient.WeightDividerTooltip")}
                />
            </Grid>
            <Grid item lg={3} sm={4} xs={12}>
                <FormField
                    name="countryCodeMode"
                    title={t("ExternalApiClient.countryCodeMode")}
                    type="select"
                    selectProps={countryCodeModeProps}
                    dataTip={t("ExternalApiClient.CountryCodeModeTooltip")}
                />
                <Typography variant="caption" color="textSecondary" component="div" style={{marginTop: 4, lineHeight: 1.3}}>
                    {t("ExternalApiClient.CountryCodeModeHelp")}
                </Typography>
            </Grid>
            <Grid item lg={3} sm={4} xs={12}>
                <FormField
                    name="dateAlignmentMode"
                    title={t("ExternalApiClient.dateAlignmentMode")}
                    type="select"
                    selectProps={dateAlignmentModeProps}
                    dataTip={t("ExternalApiClient.DateAlignmentModeTooltip")}
                />
                <Typography variant="caption" color="textSecondary" component="div" style={{marginTop: 4, lineHeight: 1.3}}>
                    {t("ExternalApiClient.DateAlignmentModeHelp")}
                </Typography>
            </Grid>
            <Grid item lg={3} sm={4} xs={6}>
                <FormField
                    name="addEmailToOffer"
                    title={t("ExternalApiClient.addEmailToOffer")}
                    type="checkbox"
                    dataTip={t("ExternalApiClient.AddEmailToOfferTooltip")}
                />
                <Typography variant="caption" color="textSecondary" component="div" style={{marginTop: 4, lineHeight: 1.3}}>
                    {t("ExternalApiClient.AddEmailToOfferHelp")}
                </Typography>
            </Grid>

            <Grid item xs={12}>
                <ExternalApiCityOverridesEditor />
            </Grid>

            {/* === Síť === */}
            <Grid item xs={12}>
                <SectionHeader title={t("ExternalApiClient.SectionNetwork")} description={t("ExternalApiClient.SectionNetworkDescription")}/>
            </Grid>
            <Grid item lg={3} sm={4} xs={12}>
                <FormField
                    name="ipAccessMode"
                    title={t("ExternalApiClient.ipAccessMode")}
                    type="select"
                    selectProps={ipAccessModeProps}
                    dataTip={t("ExternalApiClient.IpAccessModeTooltip")}
                />
            </Grid>

            <Grid item xs={12}>
                <ExternalApiAllowedIpsEditor />
            </Grid>

            {/* === API definice — examples + download === */}
            {canDownloadOpenApi && (
                <>
                    <Grid item xs={12}>
                        <SectionHeader title={t("ExternalApiClient.SectionApiDefinition")} description={t("ExternalApiClient.SectionApiDefinitionDescription")}/>
                    </Grid>
                    <Grid item lg={3} sm={4} xs={12}>
                        <FormField
                            name="exampleLogin"
                            title={t("ExternalApiClient.exampleLogin")}
                            type="text"
                            textFieldProps={{inputProps: {maxLength: 64}}}
                            dataTip={t("ExternalApiClient.ExampleLoginTooltip")}
                        />
                    </Grid>
                    <Grid item lg={3} sm={4} xs={12}>
                        <FormField
                            name="exampleContactName"
                            title={t("ExternalApiClient.exampleContactName")}
                            type="text"
                            textFieldProps={{inputProps: {maxLength: 255}}}
                            dataTip={t("ExternalApiClient.ExampleContactNameTooltip")}
                        />
                    </Grid>
                    <Grid item lg={3} sm={4} xs={12}>
                        <FormField
                            name="exampleContactEmail"
                            title={t("ExternalApiClient.exampleContactEmail")}
                            type="text"
                            textFieldProps={{inputProps: {maxLength: 255}}}
                            dataTip={t("ExternalApiClient.ExampleContactEmailTooltip")}
                        />
                    </Grid>
                    <Grid item lg={3} sm={4} xs={12}>
                        <FormField
                            name="exampleContactPhone"
                            title={t("ExternalApiClient.exampleContactPhone")}
                            type="text"
                            textFieldProps={{inputProps: {maxLength: 64}}}
                            dataTip={t("ExternalApiClient.ExampleContactPhoneTooltip")}
                        />
                    </Grid>
                    <Grid item lg={3} sm={4} xs={12}>
                        <FormField
                            name="exampleContactMobile"
                            title={t("ExternalApiClient.exampleContactMobile")}
                            type="text"
                            textFieldProps={{inputProps: {maxLength: 64}}}
                            dataTip={t("ExternalApiClient.ExampleContactMobileTooltip")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="body2" color="textSecondary" style={{marginBottom: 8}}>
                            {t("ExternalApiClient.DownloadHint")}
                        </Typography>
                        <Button
                            variant="outlined"
                            startIcon={<GetAppIcon/>}
                            style={{marginRight: 8}}
                            onClick={() => downloadFileSimple(`admin/external-api-client/${data!.id}/openapi.yaml?lang=cs`, errorTranslator)}
                        >
                            {t("ExternalApiClient.DownloadApiDefinitionCs")}
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<GetAppIcon/>}
                            onClick={() => downloadFileSimple(`admin/external-api-client/${data!.id}/openapi.yaml?lang=en`, errorTranslator)}
                        >
                            {t("ExternalApiClient.DownloadApiDefinitionEn")}
                        </Button>
                    </Grid>

                    {data?.enabled && (
                        <Grid item xs={12}>
                            <ExternalApiTester client={data!}/>
                        </Grid>
                    )}
                </>
            )}

            {/* === Limity === */}
            <Grid item xs={12}>
                <SectionHeader title={t("ExternalApiClient.SectionLimits")} description={t("ExternalApiClient.SectionLimitsDescription")}/>
            </Grid>
            <Grid item lg={3} sm={4} xs={6}>
                <FormField
                    name="rateLimitPerMin"
                    title={t("ExternalApiClient.rateLimitPerMin")}
                    type="number"
                    dataTip={t("ExternalApiClient.RateLimitTooltip")}
                />
            </Grid>
            <Grid item lg={3} sm={4} xs={6}>
                <FormField
                    name="maxPayloadBytes"
                    title={t("ExternalApiClient.maxPayloadBytes")}
                    type="number"
                    dataTip={t("ExternalApiClient.MaxPayloadBytesTooltip")}
                />
            </Grid>
        </Grid>
    );
};

export function ExternalApiClientDial(props: RouteComponentProps) {
    const {t} = useTranslation();
    return <Dial<ExternalApiClient>
        mode={props.mode}
        config={{
            tableTitle: t("Dials.ExternalApiClients"),
            endpoint: "admin/external-api-client",
            clazz: ExternalApiClient,
            filtering: true,
            columns: [
                {
                    title: t("ExternalApiClient.slug"),
                    field: "slug",
                    defaultSort: "asc",
                    filterProps: () => ({type: FormInputType.Text})
                },
                {
                    title: t("ExternalApiClient.name"),
                    field: "name",
                    filterProps: () => ({type: FormInputType.Text})
                },
                {
                    title: t("ExternalApiClient.enabled"),
                    field: "enabled",
                    render: (d: ExternalApiClient) => <CheckMark checked={d.enabled}/>
                },
                {
                    title: t("ExternalApiClient.dtoVariant"),
                    field: "dtoVariant"
                },
                {
                    title: t("ExternalApiClient.provozovnaKod"),
                    field: "provozovnaKod",
                    render: (d: ExternalApiClient) => d.provozovnaKod ? `${d.provozovnaKod} (#${d.provozovnaId})` : ""
                },
                {
                    title: t("ExternalApiClient.ipAccessMode"),
                    field: "ipAccessMode"
                },
            ]
        }}
        hideNewButtonOnEdit
        focusFieldWhenMounted
        crudConfig={{editEnabled: true, removeEnabled: true, addEnabled: true}}
        modalHeaderName={() => t("ExternalApiClient.ModalHeader")}
        createInstance={() => new ExternalApiClient()}
        layoutForm={() => <ExternalApiClientForm/>}
    />;
}
